import React, { useEffect, useState } from 'react';
import { HiOutlineEye, HiOutlineX, HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineSearch, HiOutlineFilter } from 'react-icons/hi';

// Tree node tipi: { id, name, level, children: [], products: [] }
function buildTree(groups, products) {
  // Veri kontrolü
  if (!Array.isArray(groups)) {
    console.warn('groups is not an array:', groups);
    return [];
  }
  if (!Array.isArray(products)) {
    console.warn('products is not an array:', products);
    return [];
  }

  const groupMap = {};
  groups.forEach(g => {
    groupMap[g.id] = { ...g, children: [], products: [] };
  });
  // Grupları hiyerarşik olarak bağla
  const rootGroups = [];
  groups.forEach(g => {
    if (g.parent_id) {
      groupMap[g.parent_id].children.push(groupMap[g.id]);
    } else {
      rootGroups.push(groupMap[g.id]);
    }
  });
  // Ürünleri gruplara ekle
  products.forEach(p => {
    if (p.group_id && groupMap[p.group_id]) {
      groupMap[p.group_id].products.push(p);
    }
  });
  return rootGroups;
}

const SutKutuphanesiPage = () => {
  const [tree, setTree] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Arama ve filtreleme state'leri
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all'); // all, sut_code, description, aciklama
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  
  // Sayfalandırma state'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [groupsRes, productsRes] = await Promise.all([
          fetch('/api/sut-groups').then(r => r.json()),
          fetch('/api/sut-prices?limit=10000').then(r => r.json())
        ]);
        
        // API yanıtlarını kontrol et
        const groups = Array.isArray(groupsRes) ? groupsRes : (groupsRes.data || []);
        const products = Array.isArray(productsRes) ? productsRes : (productsRes.data || []);
        
        setAllProducts(products);
        
        // Eğer grup yoksa, ürünleri düz liste olarak göster
        if (groups.length === 0 && products.length > 0) {
          setTree([{
            id: 'root',
            name: 'Tüm SUT Fiyatları',
            level: 1,
            children: [],
            products: products
          }]);
        } else {
          setTree(buildTree(groups, products));
        }
      } catch (error) {
        console.error('SUT Kütüphanesi veri yükleme hatası:', error);
        setTree([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Filtreleme fonksiyonu
  useEffect(() => {
    let filtered = allProducts;

    // Arama filtresi
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => {
        const sutCode = (product.sut_code || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        const aciklama = (product.aciklama || '').toLowerCase();
        
        switch (searchType) {
          case 'sut_code':
            return sutCode.includes(term);
          case 'description':
            return description.includes(term);
          case 'aciklama':
            return aciklama.includes(term);
          default:
            return sutCode.includes(term) || description.includes(term) || aciklama.includes(term);
        }
      });
    }

    // Fiyat aralığı filtresi
    if (priceRange.min || priceRange.max) {
      filtered = filtered.filter(product => {
        const price = Number(product.price);
        const min = priceRange.min ? Number(priceRange.min) : 0;
        const max = priceRange.max ? Number(priceRange.max) : Infinity;
        return price >= min && price <= max;
      });
    }

    setFilteredProducts(filtered);
    setCurrentPage(1); // Filtreleme sonrası ilk sayfaya dön
  }, [searchTerm, searchType, priceRange, allProducts]);

  // Sayfalandırma
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Filtrelenmiş ürünleri tree'ye ekle
  const filteredTree = tree.map(group => ({
    ...group,
    products: group.products.filter(product => 
      currentProducts.some(fp => fp.id === product.id)
    )
  }));

  const toggle = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const formatPrice = (price) => {
    return Number(price).toLocaleString('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const showProductDetail = (product) => {
    setSelectedProduct(product);
    setShowDetailModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchType('all');
    setPriceRange({ min: '', max: '' });
  };

  const renderTree = (nodes, level = 1) => (
    <div className={`${level > 1 ? 'ml-6 border-l-2 border-blue-100 pl-4' : ''}`}>
      {nodes.map(node => (
        <div key={node.id} className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            {node.children.length > 0 && (
              <button 
                onClick={() => toggle(node.id)} 
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                {expanded[node.id] ? 
                  <HiOutlineChevronDown className="w-4 h-4" /> : 
                  <HiOutlineChevronRight className="w-4 h-4" />
                }
              </button>
            )}
            <span className="font-semibold text-gray-800 text-sm">{node.name}</span>
            <span className="text-xs text-gray-400">(Seviye {node.level})</span>
          </div>
          
          {/* Alt gruplar */}
          {node.children.length > 0 && expanded[node.id] && renderTree(node.children, level + 1)}
          
          {/* Ürünler */}
          {node.products.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">SUT Kodu</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Tanım</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Açıklama</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Fiyat</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {node.products.map(p => (
                      <tr key={p.id} className="hover:bg-blue-50 transition-colors">
                        <td className="px-3 py-2 font-mono text-blue-600 font-medium">
                          {p.sut_code}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {truncateText(p.description || (p.row_info ? JSON.parse(p.row_info)[1] : ''), 60)}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {truncateText(p.aciklama || (p.row_info ? JSON.parse(p.row_info)[2] : ''), 40)}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-green-700">
                          {formatPrice(p.price)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => showProductDetail(p)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Detayları görüntüle"
                          >
                            <HiOutlineEye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">SUT Kütüphanesi</h1>
                <p className="text-sm text-gray-600 mt-1">
                  SUT fiyatları ve detayları
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <HiOutlineFilter className="w-4 h-4" />
                  Gelişmiş Arama
                </button>
              </div>
            </div>
          </div>

          {/* Arama Bölümü */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="SUT kodu, tanım veya açıklama ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tüm Alanlar</option>
                <option value="sut_code">SUT Kodu</option>
                <option value="description">Tanım</option>
                <option value="aciklama">Açıklama</option>
              </select>
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Temizle
              </button>
            </div>

            {/* Gelişmiş Arama */}
            {showAdvancedSearch && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Fiyat Aralığı</h4>
                <div className="flex items-center gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Min Fiyat (TL)</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={priceRange.min}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Max Fiyat (TL)</label>
                    <input
                      type="number"
                      placeholder="∞"
                      value={priceRange.max}
                      onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-6">
            {/* Sonuç Bilgisi */}
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {filteredProducts.length} sonuç bulundu
                {searchTerm && ` "${searchTerm}" için`}
              </div>
              {totalPages > 1 && (
                <div className="text-sm text-gray-600">
                  Sayfa {currentPage} / {totalPages}
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Yükleniyor...</span>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Sonuç bulunamadı</h3>
                <p className="text-gray-600">
                  {searchTerm ? `"${searchTerm}" için sonuç bulunamadı.` : 'Henüz SUT fiyatı yüklenmemiş.'}
                </p>
              </div>
            ) : (
              <>
                {renderTree(filteredTree)}
                
                {/* Sayfalandırma */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-center">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        İlk
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Önceki
                      </button>
                      
                      {/* Sayfa Numaraları */}
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-2 text-sm border rounded-lg ${
                                currentPage === pageNum
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Sonraki
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Son
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detay Modal */}
      {showDetailModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">SUT Detayları</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <HiOutlineX className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SUT Kodu</label>
                  <div className="font-mono text-blue-600 font-semibold text-lg">
                    {selectedProduct.sut_code}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fiyat</label>
                  <div className="text-green-700 font-semibold text-lg">
                    {formatPrice(selectedProduct.price)}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tanım</label>
                <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                  {selectedProduct.description || (selectedProduct.row_info ? JSON.parse(selectedProduct.row_info)[1] : 'Tanım bulunamadı')}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <div className="bg-gray-50 rounded-lg p-3 text-gray-800">
                  {selectedProduct.aciklama || (selectedProduct.row_info ? JSON.parse(selectedProduct.row_info)[2] : 'Açıklama bulunamadı')}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kaynak Dosya</label>
                  <div className="text-sm text-gray-600">
                    {selectedProduct.source_file}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eklenme Tarihi</label>
                  <div className="text-sm text-gray-600">
                    {new Date(selectedProduct.created_at).toLocaleDateString('tr-TR')}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SutKutuphanesiPage; 